<template>
  <div class="word-hunt">
    <LevelComplete v-if="isGameComplete" />
    <div v-else>
      <h1
        class="target-emoji"
        @dragover.prevent
        @drop="handleDrop"
        @click="speakOutLoud(targetWord)"
      >
        {{ targetEmoji }}
      </h1>
      <p v-if="message" class="message">
        {{ message }}
      </p>
      <div class="grid">
        <div
          v-for="(word, index) in shuffledWords"
          :key="index"
          class="word-draggable"
          :style="draggedWordIndex === index ? draggedWordStyle : {}"
          draggable="true"
          @dragstart="handleDrag($event, word)"
          @click="speakOutLoud(word)"
          @touchstart="handleTouchStart($event, word, index)"
          @touchmove="handleTouchMove($event)"
          @touchend="handleTouchEnd($event)"
        >
          {{ word }}
        </div>
      </div>
      <div class="progress">
        <p>{{ progressMessage }}</p>
      </div>
    </div>
  </div>
</template>

<script>
import LevelComplete from "../LevelComplete.vue";
import { speechMixin } from "@/mixins/speechMixin"; // update the path as needed

export default {
  mixins: [speechMixin],
  components: {
    LevelComplete,
  },
  data() {
    return {
      words: [
        { word: "apple", emoji: "🍎" },
        { word: "banana", emoji: "🍌" },
        { word: "cat", emoji: "🐱" },
        { word: "dog", emoji: "🐶" },
        { word: "sun", emoji: "☀️" },
        { word: "moon", emoji: "🌙" },
        { word: "star", emoji: "⭐" },
        { word: "heart", emoji: "❤️" },
        { word: "car", emoji: "🚗" },
        { word: "house", emoji: "🏠" },
      ],
      targetWord: "",
      targetEmoji: "",
      shuffledWords: [],
      message: "",
      isGameComplete: false,
      // Removed selectedVoice (handled by mixin)
      touchStartX: 0,
      touchStartY: 0,
      draggedWord: null,
      draggedWordIndex: null,
      draggedWordStyle: {},
      wordCounts: {},
      maxCount: 1,
      isDragging: false,
    };
  },
  computed: {
    progressMessage() {
      const totalFound = Object.values(this.wordCounts).reduce((a, b) => a + b, 0);
      const totalWords = this.words.length * this.maxCount;
      return `Words found: ${totalFound} / ${totalWords}`;
    },
  },
  mounted() {
    this.initializeWordCounts();
    this.setupGame();
    // Initialize voices via mixin
    this.setupVoices();
  },
  methods: {
    initializeWordCounts() {
      this.wordCounts = this.words.reduce((acc, wordObj) => {
        acc[wordObj.word] = 0;
        return acc;
      }, {});
    },
    setupGame() {
      const availableWords = this.words.filter(
        (wordObj) => this.wordCounts[wordObj.word] < this.maxCount
      );
      if (availableWords.length === 0) {
        this.playSound("https://codeskulptor-demos.commondatastorage.googleapis.com/descent/gotitem.mp3");
        this.speakOutLoud("Great Job!");
        this.isGameComplete = true;
        setTimeout(() => {
          this.$router.push("/"); // Adjust the route as necessary
        }, 4000);
        return;
      }
      const targetObj = availableWords[Math.floor(Math.random() * availableWords.length)];
      this.targetWord = targetObj.word;
      this.targetEmoji = targetObj.emoji;
      // Create an array of words excluding the target word
      const otherWords = this.words
        .filter((wordObj) => wordObj.word !== this.targetWord)
        .map((wordObj) => wordObj.word);
      if (otherWords.length === 0) {
        otherWords.push(this.targetWord);
      }

      this.speakOutLoud(this.targetWord);
      // Randomly choose words from otherWords
      const randomWords = [];
      const poolSize = Math.min(11, otherWords.length);
      for (let i = 0; i < poolSize; i++) {
        const randomWord = otherWords[Math.floor(Math.random() * otherWords.length)];
        randomWords.push(randomWord);
      }
      // Add target word to the pool
      randomWords.push(this.targetWord);
      // Remove duplicates and shuffle
      const uniqueWords = Array.from(new Set(randomWords));
      this.shuffledWords = uniqueWords.sort(() => Math.random() - 0.5);
      this.message = "";
    },
    handleDrag(event, word) {
      event.dataTransfer.setData("text/plain", word);
    },
    handleDrop(event) {
      const droppedWord = event.dataTransfer.getData("text/plain");
      if (droppedWord === this.targetWord) {
        this.playSound("https://commondatastorage.googleapis.com/codeskulptor-assets/week7-brrring.m4a");
        this.speakOutLoud(this.targetWord);
        this.message = "✅";
        this.wordCounts[this.targetWord]++;
        setTimeout(() => {
          this.resetGame();
        }, 3000);
      } else {
        this.message = "❌";
        setTimeout(() => {
          this.message = "";
        }, 3000);
      }
    },
    handleTouchStart(event, word, index) {
      event.preventDefault();
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.isDragging = true;
      this.draggedWord = word;
      this.draggedWordIndex = index;
      this.draggedWordStyle = {
        position: "relative",
        zIndex: 1000,
      };
    },
    handleTouchMove(event) {
      if (!this.isDragging) return;
      event.preventDefault(); // Prevent scrolling
      const touchX = event.touches[0].clientX;
      const touchY = event.touches[0].clientY;
      const deltaX = touchX - this.touchStartX;
      const deltaY = touchY - this.touchStartY;
      this.draggedWordStyle = {
        ...this.draggedWordStyle,
        transform: `translate(${deltaX}px, ${deltaY}px)`,
        transition: "none",
      };
    },
    handleTouchEnd(event) {
      if (!this.isDragging) return;
      const dropZone = this.$el.querySelector(".target-emoji");
      const rect = dropZone.getBoundingClientRect();
      const touchEndX = event.changedTouches[0].clientX;
      const touchEndY = event.changedTouches[0].clientY;
      if (
        touchEndX >= rect.left &&
        touchEndX <= rect.right &&
        touchEndY >= rect.top &&
        touchEndY <= rect.bottom
      ) {
        this.handleDrop({ dataTransfer: { getData: () => this.draggedWord } });
      }
      this.isDragging = false;
      this.draggedWord = null;
      this.draggedWordIndex = null;
      this.draggedWordStyle = {};
    },
    playSound(url) {
      const audio = new Audio(url);
      audio.play();
    },
    resetGame() {
      this.setupGame();
    },
  },
};
</script>

<style scoped>
.word-hunt {
  text-align: center;
}

.progress {
  text-align: center;
  font-size: 1.5rem;
  margin-bottom: 20px;
}

.grid {
  max-width: 500px;
  padding: 50px;
  margin: auto;
  margin-bottom: 30px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: 1rem;
}

.target-emoji {
  font-size: 5rem;
  margin-bottom: 20px;
  cursor: pointer;
  transition: all 0.3s;
}

.word-draggable {
  cursor: grab;
  border-radius: 10px;
  padding: 10px;
  font-size: 1.5rem;
  background-color: #81d4fa;
  font-weight: bold;
  text-align: center;
  transition: transform 0.3s;
  box-shadow: -2px 2px 2px 2px lightgray;
  user-select: none;
}

.word-draggable:hover {
  transform: scale(1.1);
}

.message {
  font-size: 3rem;
  font-weight: bold;
  text-align: center;
}
</style>
